// ---------------------------------------------------------------------------
// Canal Mercado Libre — lógica pura.
//
// Todo lo que se puede razonar sin red vive acá, para que sea testeable y para
// que las edge functions y el front compartan exactamente las mismas reglas.
//
// Ver PLAN_MERCADO_LIBRE.md (raíz) para el diseño y las nueve decisiones.
// ---------------------------------------------------------------------------

export const ML_API = 'https://api.mercadolibre.com';

/** Margen antes del vencimiento del access token. El token dura 6 horas. */
export const REFRESH_MARGIN_MS = 15 * 60 * 1000;

/** Cuánto dura el lock de refresco. Si un proceso muere, otro puede reintentar. */
export const REFRESH_LOCK_MS = 60 * 1000;

/** Canal con el que entran las ventas de ML (ver crear_venta_canal). */
export const ML_CHANNEL = 'mercadolibre';

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

/**
 * Unidades a publicar en Mercado Libre: stock local menos el colchón, con piso
 * en cero.
 *
 * El colchón existe porque con available_quantity=0 Mercado Libre pausa la
 * publicación por su cuenta (queda out_of_stock) y recuperarla no siempre es
 * automático. También da margen contra la sobreventa: el último que se vende en
 * el mostrador no queda publicado en ML.
 *
 * El stock local PUEDE ser negativo (venta con stock negativo permitido), pero a
 * ML no se le puede mandar una cantidad negativa.
 */
export function calcularStockPublicable(stockLocal, colchon = 0) {
  const stock = Number(stockLocal);
  const buffer = Number(colchon);
  const base = Number.isFinite(stock) ? Math.floor(stock) : 0;
  const reserva = Number.isFinite(buffer) ? Math.floor(buffer) : 0;
  return Math.max(0, base - reserva);
}

/**
 * Body del PUT /items/{id} para actualizar stock.
 *
 * REGLA CRÍTICA: si la publicación tiene variaciones y el array `variations` no
 * incluye TODAS las que existen, Mercado Libre BORRA las que faltan. Está en su
 * documentación, textual. Por eso este helper exige el item completo tal como lo
 * devuelve ML (un GET previo) y reconstruye el array entero, cambiando solo las
 * cantidades de las variaciones vinculadas.
 *
 * Un bug acá no devuelve error: destruye publicaciones de un cliente real.
 *
 * @param {object} itemActual  respuesta de GET /items/{id}
 * @param {Array<{ml_variation_id: string|null, cantidad: number}>} cambios
 */
export function buildStockPayload(itemActual, cambios = []) {
  if (!itemActual || typeof itemActual !== 'object') {
    throw new Error(
      'buildStockPayload: hace falta el item completo de Mercado Libre. Sin el GET previo, un PUT parcial borra variaciones.',
    );
  }

  const variaciones = Array.isArray(itemActual.variations) ? itemActual.variations : [];

  // Publicación sin variaciones: cantidad plana.
  if (variaciones.length === 0) {
    const cambio = cambios.find((c) => !c?.ml_variation_id) || cambios[0];
    return { available_quantity: Math.max(0, Number(cambio?.cantidad) || 0) };
  }

  // Con variaciones: se devuelven TODAS, con solo id y available_quantity.
  // Reenviar attribute_combinations u otros campos puede alterar la publicación.
  const porId = new Map(
    cambios
      .filter((c) => c?.ml_variation_id != null)
      .map((c) => [String(c.ml_variation_id), Math.max(0, Number(c.cantidad) || 0)]),
  );

  return {
    variations: variaciones.map((v) => {
      const nueva = porId.get(String(v.id));
      return {
        id: v.id,
        available_quantity: nueva !== undefined ? nueva : Number(v.available_quantity) || 0,
      };
    }),
  };
}

/** Body del PUT /items/{id} para actualizar precio. */
export function buildPricePayload(precio) {
  const valor = Number(precio);
  if (!Number.isFinite(valor) || valor <= 0) {
    throw new Error(`Precio inválido para publicar en Mercado Libre: ${precio}`);
  }
  return { price: Math.round((valor + Number.EPSILON) * 100) / 100 };
}

// ---------------------------------------------------------------------------
// Órdenes
// ---------------------------------------------------------------------------

/**
 * Encuentra la publicación vinculada de un item de la orden.
 *
 * Sin vínculo devuelve null y NO adivina: una publicación vinculada en otra
 * variación no sirve para esta, y descontar el producto equivocado es peor que
 * no procesar la orden.
 */
export function resolveListingParaItem(orderItem, listings = []) {
  const itemId = orderItem?.item?.id;
  if (!itemId) return null;

  // En GET /orders/{id} la variación viene dentro de `item`; se acepta también
  // en el nivel de arriba por compatibilidad.
  const rawVar = orderItem?.item?.variation_id ?? orderItem?.variation_id;
  const variationId = rawVar != null ? String(rawVar) : null;

  return (
    listings.find((l) => {
      if (l?.ml_item_id !== itemId) return false;
      const lVar = l?.ml_variation_id != null ? String(l.ml_variation_id) : null;
      return lVar === variationId;
    }) || null
  );
}

/**
 * Traduce una orden de Mercado Libre a los items que espera crear_venta_canal.
 *
 * Si CUALQUIER item no está vinculado, se rechaza la orden entera: media venta
 * descontaría el stock de un producto y del otro no, y el total no cerraría con
 * lo que Mercado Libre efectivamente cobró. La orden queda en la bandeja para
 * que el comercio vincule el producto y se reprocese.
 */
export function mapOrderAItemsDeVenta(order, listings = []) {
  const orderItems = Array.isArray(order?.order_items) ? order.order_items : [];

  if (orderItems.length === 0) {
    return { ok: false, reason: 'sin_items', items: null, itemsSinVincular: [], total: 0 };
  }

  const items = [];
  const sinVincular = [];

  for (const oi of orderItems) {
    const listing = resolveListingParaItem(oi, listings);
    if (!listing) {
      sinVincular.push(oi?.item?.id || 'desconocido');
      continue;
    }
    items.push({
      product_id: listing.product_id,
      variant_id: listing.variant_id ?? null,
      quantity: Number(oi.quantity) || 0,
      unit_price: Number(oi.unit_price) || 0,
      product_name: oi?.item?.title || 'Producto',
    });
  }

  if (sinVincular.length > 0) {
    return {
      ok: false,
      reason: 'sin_vincular',
      items: null,
      itemsSinVincular: sinVincular,
      total: 0,
    };
  }

  const total = items.reduce((acc, i) => acc + i.quantity * i.unit_price, 0);
  return { ok: true, reason: null, items, itemsSinVincular: [], total };
}

/**
 * ¿La orden está efectivamente cobrada?
 *
 * 'confirmed' es el estado inicial y todavía no tiene el pago acreditado: crear
 * la venta ahí descontaría stock por algo que puede no pagarse nunca.
 */
export function isOrderCobrada(order) {
  return String(order?.status || '').toLowerCase() === 'paid';
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

/**
 * ¿Hay que refrescar el access token de esta cuenta?
 *
 * El access token dura 6 horas y el refresh token es DE UN SOLO USO: cada
 * refresco devuelve uno nuevo y anula el anterior. Si dos procesos refrescan a
 * la vez, uno se queda con un token muerto y el comercio queda desconectado de
 * Mercado Libre hasta que vuelva a autorizar a mano.
 *
 * Por eso: se refresca con margen (para no correr contra el vencimiento) y solo
 * si no hay otro proceso con el lock tomado.
 */
export function necesitaRefresco(cuenta, ahora = new Date()) {
  if (!cuenta) return true;

  const now = ahora instanceof Date ? ahora.getTime() : new Date(ahora).getTime();

  // Otro proceso está refrescando: no competimos con él.
  const lock = cuenta.refresh_lock_until ? new Date(cuenta.refresh_lock_until).getTime() : null;
  if (lock && Number.isFinite(lock) && lock > now) return false;

  const vence = cuenta.access_expires_at ? new Date(cuenta.access_expires_at).getTime() : null;
  if (!vence || !Number.isFinite(vence)) return true;

  return vence - now <= REFRESH_MARGIN_MS;
}

/**
 * Cada cuánto se refresca aunque el access token siga vigente. El refresh
 * token vence a los 6 meses SIN USO: una cuenta sin ventas ni cambios de stock
 * se desconectaría sola. Refrescar cada 30 días lo mantiene vivo.
 */
export const REFRESH_MAX_AGE_MS = 30 * 24 * 3600 * 1000;

/**
 * ¿Hay que refrescar estos tokens? A diferencia de necesitaRefresco, mira el
 * JSON guardado en el vault ({ expires_at, refreshed_at }), no la fila de la
 * cuenta, y no sabe nada del lock (eso lo resuelve ml_try_refresh_lock).
 */
export function tokenNecesitaRefresco(tokens, ahora = new Date()) {
  if (!tokens || typeof tokens !== 'object') return true;
  const now = ahora instanceof Date ? ahora.getTime() : new Date(ahora).getTime();
  const vence = new Date(tokens.expires_at).getTime();
  if (!Number.isFinite(vence)) return true;
  if (vence - now <= REFRESH_MARGIN_MS) return true;
  const refrescado = new Date(tokens.refreshed_at).getTime();
  if (Number.isFinite(refrescado) && now - refrescado >= REFRESH_MAX_AGE_MS) return true;
  return false;
}

// ---------------------------------------------------------------------------
// OAuth (PKCE S256, RFC 7636)
// ---------------------------------------------------------------------------

function base64Url(bytes) {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** code_verifier: 64 caracteres del alfabeto que permite el RFC. */
export function generarCodeVerifier() {
  const bytes = new Uint8Array(48);
  globalThis.crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** code_challenge = BASE64URL(SHA256(verifier)). */
export async function pkceChallengeS256(verifier) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(verifier)));
  return base64Url(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// Webhook
// ---------------------------------------------------------------------------

/**
 * El webhook de ML no trae datos ni firma: solo `resource` ("/orders/123").
 * Se extrae el id y la orden se relee con el token del vendedor. Cualquier
 * otra forma se descarta: no se interpreta lo que no se entiende.
 */
export function parseOrderIdFromResource(resource) {
  if (typeof resource !== 'string') return null;
  const m = /^\/?orders\/(\d+)$/.exec(resource.trim());
  return m ? m[1] : null;
}

// ---------------------------------------------------------------------------
// Publicaciones
// ---------------------------------------------------------------------------

/**
 * SKU de un item o de una variación: seller_custom_field, o el atributo
 * SELLER_SKU. Sin SKU devuelve null y la publicación queda para vincular a mano.
 */
export function extractSku(entidad) {
  if (!entidad || typeof entidad !== 'object') return null;
  const directo = typeof entidad.seller_custom_field === 'string' ? entidad.seller_custom_field.trim() : '';
  if (directo) return directo;
  const attrs = Array.isArray(entidad.attributes) ? entidad.attributes : [];
  const attr = attrs.find((a) => a?.id === 'SELLER_SKU');
  const valor = typeof attr?.value_name === 'string' ? attr.value_name.trim() : '';
  return valor || null;
}

/**
 * Un item de ML (GET /items/{id}) a las filas de ml_listings: una por
 * variación, o una sola si no tiene variaciones.
 */
export function itemToListingRows(item) {
  if (!item || typeof item !== 'object' || !item.id) return [];
  const base = {
    ml_item_id: String(item.id),
    ml_status: item.status ?? null,
    ml_price: item.price != null && Number.isFinite(Number(item.price)) ? Number(item.price) : null,
    permalink: item.permalink ?? null,
    thumbnail: item.thumbnail ?? null,
  };
  const variaciones = Array.isArray(item.variations) ? item.variations : [];
  const titulo = item.title ?? null;

  if (variaciones.length === 0) {
    return [{
      ml_item_id: base.ml_item_id,
      ml_variation_id: null,
      title: titulo,
      sku: extractSku(item),
      ml_status: base.ml_status,
      ml_price: base.ml_price,
      ml_available_quantity: Number.isFinite(Number(item.available_quantity)) ? Number(item.available_quantity) : null,
      permalink: base.permalink,
      thumbnail: base.thumbnail,
    }];
  }

  return variaciones.map((v) => {
    const combinacion = (Array.isArray(v?.attribute_combinations) ? v.attribute_combinations : [])
      .map((c) => [c?.name, c?.value_name].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(', ');
    return {
      ml_item_id: base.ml_item_id,
      ml_variation_id: String(v.id),
      title: combinacion ? `${titulo ?? ''} — ${combinacion}` : titulo,
      sku: extractSku(v),
      ml_status: base.ml_status,
      ml_price: v?.price != null && Number.isFinite(Number(v.price)) ? Number(v.price) : base.ml_price,
      ml_available_quantity: Number.isFinite(Number(v?.available_quantity)) ? Number(v.available_quantity) : null,
      permalink: base.permalink,
      thumbnail: base.thumbnail,
    };
  });
}

/**
 * Body del PUT /items/{id} para el precio.
 *
 * Misma regla de oro que el stock: con variaciones, el array va COMPLETO
 * (todas con el mismo precio; ML no permite precios distintos por variación).
 */
export function buildPricePayloadParaItem(itemActual, precio) {
  if (!itemActual || typeof itemActual !== 'object') {
    throw new Error('buildPricePayloadParaItem: hace falta el item completo de Mercado Libre.');
  }
  const { price } = buildPricePayload(precio);
  const variaciones = Array.isArray(itemActual.variations) ? itemActual.variations : [];
  if (variaciones.length === 0) return { price };
  return { variations: variaciones.map((v) => ({ id: v.id, price })) };
}

/** Agrupa los targets de sincronización por publicación: un PUT por item. */
export function agruparTargetsPorItem(targets = []) {
  const grupos = new Map();
  for (const t of Array.isArray(targets) ? targets : []) {
    if (!t?.ml_item_id) continue;
    const key = String(t.ml_item_id);
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(t);
  }
  return grupos;
}

// ---------------------------------------------------------------------------
// Etiquetas de la pantalla
// ---------------------------------------------------------------------------

export const ML_ORDER_STATUS_LABELS = {
  pendiente: 'Esperando el pago',
  procesada: 'Registrada como venta',
  sin_vincular: 'Tiene publicaciones sin vincular',
  error: 'Error',
  cancelada: 'Cancelada',
  conflicto: 'Cancelada después de registrarse',
  ignorada: 'Anterior a la conexión',
};

export const ML_MOTIVO_LABELS = {
  archivado: 'El producto está archivado',
  inactivo: 'El producto está inactivo',
  combo_dinamico: 'Los combos dinámicos no tienen stock propio',
  variantes_sin_stock_propio: 'El producto tiene variantes: el ERP lleva un solo stock por producto',
  sin_sucursal: 'Falta elegir la sucursal',
  sin_lista: 'Falta elegir la lista de precios del canal',
  precio_invalido: 'El precio calculado es cero o negativo',
  item_con_variaciones: 'La publicación tiene variaciones: vinculá cada variación',
  precios_distintos: 'Las variaciones vinculadas darían precios distintos (ML exige uno solo)',
};
